// ===== K-Tec Public AI Chat Widget =====
// Floating AI chat for the public website — premium Apple-inspired design

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
  Bot, X, Minimize2, MessageSquare, Send, Mic, MicOff, Volume2, VolumeX,
  RefreshCw, ChevronRight, Sparkles, Phone, MessageCircle, 
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';

import { useAIStore } from '../../ai/aiStore';
import { useCatalogStore } from '../../admin/store/catalogStore';
import { detectLanguage, type Language } from '../../ai/languageDetector';
import { parseRequirement, mergeRequirements } from '../../ai/requirementParser';
import {
  WELCOME_MESSAGES, getDiscoveryQuestions, formatSolutionResponse,
  getServiceInfoResponse, getNoProductsResponse, getQuotationRequestMessage,
} from '../../ai/KtecAIEngine';
import { buildSolution } from '../../ai/productRecommendation';
import type { ConversationState } from '../../ai/conversationMemory';

// ── Quick Prompts ─────────────────────────────────────────────────────────────
const QUICK_PROMPTS = [
  { label: 'මගේ ගෙදරට CCTV ඕන', icon: '🏠' },
  { label: 'Shop එකට camera system', icon: '🏪' },
  { label: 'රෑට colour camera ඕන', icon: '🌙' },
  { label: 'Wi-Fi camera ඕන', icon: '📶' },
  { label: 'Starlink ගැන', icon: '🛰️' },
  { label: 'Budget eka 150k CCTV', icon: '💰' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function MessageBubble({ msg, onSpeak }: { msg: { role: string; content: string; id: string }; onSpeak?: (text: string) => void }) {
  const isUser = msg.role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-[#7FA86A] flex items-center justify-center flex-shrink-0 mt-1">
          <Bot size={14} className="text-white" />
        </div>
      )}
      <div className={`max-w-[85%] ${isUser ? 'order-first' : ''}`}>
        <div
          className={`px-3 py-2.5 rounded-2xl text-sm leading-relaxed ${
            isUser
              ? 'bg-[#7FA86A] text-white rounded-tr-sm'
              : 'bg-[#F5F5F7] text-[#1D1D1F] rounded-tl-sm'
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{msg.content}</p>
          ) : (
            <div className="prose prose-sm max-w-none prose-p:my-0.5 prose-headings:my-1 prose-ul:my-0.5 prose-li:my-0">
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            </div>
          )}
        </div>
        {!isUser && onSpeak && (
          <button
            onClick={() => onSpeak(msg.content)}
            className="mt-1 ml-1 text-[#86868B] hover:text-[#7FA86A] transition-colors"
            title="Read aloud"
          >
            <Volume2 size={11} />
          </button>
        )}
      </div>
    </motion.div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex gap-2 items-start">
      <div className="w-7 h-7 rounded-full bg-[#7FA86A] flex items-center justify-center flex-shrink-0">
        <Bot size={14} className="text-white" />
      </div>
      <div className="bg-[#F5F5F7] px-3 py-2.5 rounded-2xl rounded-tl-sm">
        <div className="flex gap-1 items-center h-4">
          {[0, 1, 2].map(i => (
            <motion.div
              key={i}
              className="w-1.5 h-1.5 bg-[#86868B] rounded-full"
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function PublicAIChat() {
  const location = useLocation();
  
  // Don't show on admin pages or login page
  const isAdminPage = location.pathname.startsWith('/admin') || 
    location.pathname === '/ktec-gate-access';
  
  if (isAdminPage) return null;

  const { isChatOpen, setActiveChatOpen, startNewConversation, conversations, activeConversationId,
    updateConversationState, addMessageToConversation, settings, createLead, getLeadByConversationId } = useAIStore();
  const { catalogProducts, packages } = useCatalogStore();

  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [quotationName, setQuotationName] = useState('');
  const [quotationPhone, setQuotationPhone] = useState('');
  const [quotationEmail, setQuotationEmail] = useState('');
  const [voiceError, setVoiceError] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const activeConversation: ConversationState | null =
    conversations.find(c => c.id === activeConversationId) || null;

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConversation?.messages, isTyping]);

  // Init conversation on open
  useEffect(() => {
    if (isChatOpen && !activeConversationId) {
      startNewConversation();
    }
  }, [isChatOpen, activeConversationId, startNewConversation]);

  // Send welcome message once
  useEffect(() => {
    if (activeConversation && activeConversation.messages.length === 0) {
      const welcomeMsg = WELCOME_MESSAGES[activeConversation.language || 'sinhala'];
      addMessageToConversation(activeConversation.id, {
        role: 'assistant',
        content: welcomeMsg,
      });
    }
  }, [activeConversation?.id]);

  // ── AI Response Logic ──────────────────────────────────────────────────────
  const processMessage = useCallback(async (text: string) => {
    if (!activeConversation) return;
    const convId = activeConversation.id;

    // Detect language
    const lang: Language = detectLanguage(text);

    // Parse requirement
    const newReq = parseRequirement(text, lang);
    const mergedReq = mergeRequirements(activeConversation.requirement, newReq);

    // Update conversation state
    updateConversationState(convId, {
      language: lang,
      requirement: mergedReq,
      conversationPhase: 'discovery',
    });

    setIsTyping(true);

    // Simulate processing delay
    await new Promise(r => setTimeout(r, 800 + Math.random() * 600));

    let response = '';
    let shouldShowSolution = false;
    let solution = null;

    // Check if phone number was provided
    const phoneMatch = text.match(/0\d{9}|07\d{8}/);
    if (phoneMatch && !activeConversation.customerPhone) {
      updateConversationState(convId, { customerPhone: phoneMatch[0], isLead: true });
      
      // Create lead if we have requirement
      if (!getLeadByConversationId(convId)) {
        const lead = useAIStore.getState().leads.find(l => l.conversationId === convId);
        if (!lead) {
          createLead({
            conversationId: convId,
            customerPhone: phoneMatch[0],
            customerName: activeConversation.customerName || 'Website Visitor',
            requirement: [
              mergedReq.serviceType,
              mergedReq.cameraQuantity ? `${mergedReq.cameraQuantity} cameras` : '',
              mergedReq.propertyType,
            ].filter(Boolean).join(', ') || 'CCTV / Security System',
            estimatedValue: activeConversation.estimatedValue,
            language: lang,
            source: 'website',
            status: 'lead',
          });
        }
      }

      if (lang === 'sinhala') response = `ස්තූතියි! 📱 ඔයාගේ phone number save කරා.\n\nK-Tec team ඉක්මණින් ඔයාව contact කරනවා.\n\nQuotation ගැන interest ද? ඔයාගේ name ද දෙන්නකො.`;
      else if (lang === 'singlish') response = `Thanks! 📱 Phone number save kara.\n\nK-Tec team contact karanawa ikmana.\n\nName eka da kiyanna?`;
      else response = `Thank you! 📱 Phone number saved.\n\nOur K-Tec team will contact you shortly.\n\nWould you also like to share your name?`;

      setIsTyping(false);
      addMessageToConversation(convId, { role: 'assistant', content: response });
      return;
    }

    // Check if name was provided (simple heuristic: short text after being asked for name)
    if (activeConversation.messages.some(m => m.content.includes('name') || m.content.includes('නම')) &&
        !activeConversation.customerName && text.trim().length > 2 && text.trim().length < 30 &&
        !text.includes(' ') === false) {
      // Might be a name — but only if it looks like a name
      const looksLikeName = /^[A-Za-z\u0D80-\u0DFF ]{2,30}$/.test(text.trim());
      if (looksLikeName) {
        updateConversationState(convId, { customerName: text.trim() });
      }
    }

    // Quotation intent
    if (/quotation|quote|estimate|kotuwan|kor?tation|estimate|ganankarana/i.test(text) ||
        /quotation|estimate|ශ්‍රේෂ්ඨ|estimate|kotuwan/i.test(text)) {
      setIsTyping(false);
      addMessageToConversation(convId, {
        role: 'assistant',
        content: getQuotationRequestMessage(lang),
      });
      setShowQuotationForm(true);
      return;
    }

    // Service-specific responses
    if (mergedReq.serviceType === 'starlink' && 
        (!mergedReq.serviceType || !(mergedReq.serviceType === 'cctv'))) {
      response = getServiceInfoResponse('starlink', lang);
    } else if (mergedReq.serviceType === 'alarm' && !mergedReq.serviceType?.includes('cctv')) {
      response = getServiceInfoResponse('alarm', lang);
    } else if (mergedReq.serviceType === 'cctv' || mergedReq.cameraQuantity) {
      // Check if we have enough info to build a solution
      const hasEnoughInfo = (mergedReq.cameraQuantity || 0) > 0 && mergedReq.propertyType;

      if (hasEnoughInfo) {
        // Build solution
        const sol = buildSolution(catalogProducts, mergedReq);
        if (sol.cameras.length > 0) {
          solution = sol;
          shouldShowSolution = true;
          response = formatSolutionResponse(sol, lang);
          updateConversationState(convId, {
            estimatedValue: sol.totalEstimate,
            conversationPhase: 'recommendation',
          });
        } else {
          response = getNoProductsResponse(lang);
        }
      } else {
        // Ask discovery questions
        const question = getDiscoveryQuestions(mergedReq, lang, activeConversation);
        if (question) {
          // Mark question as asked
          const questionKey = !mergedReq.propertyType ? 'property' :
            !mergedReq.cameraQuantity ? 'quantity' :
            !mergedReq.fullColour ? 'night' : 'budget';
          updateConversationState(convId, {
            questionsAsked: [...(activeConversation.questionsAsked || []), questionKey],
          });
          response = question;
        } else {
          // We've asked all questions, build solution
          const sol = buildSolution(catalogProducts, mergedReq);
          if (sol.cameras.length > 0) {
            solution = sol;
            shouldShowSolution = true;
            response = formatSolutionResponse(sol, lang);
            updateConversationState(convId, {
              estimatedValue: sol.totalEstimate,
              conversationPhase: 'recommendation',
            });
          } else {
            response = getNoProductsResponse(lang);
          }
        }
      }
    } else {
      // General greeting / unknown
      if (/hi|hello|ayubowan|aya|helo|sup|hey/i.test(text)) {
        response = WELCOME_MESSAGES[lang];
      } else if (/contact|call|phone|number/i.test(text)) {
        if (lang === 'sinhala') response = `📞 K-Tec Contact:\n\n• **Phone:** ${settings.contactNumber}\n• **WhatsApp:** ${settings.whatsappNumber}\n• **Working hours:** 8:30AM - 6:00PM\n\nOr ask me anything about our products!`;
        else response = `📞 K-Tec Contact:\n\n• **Phone:** ${settings.contactNumber}\n• **WhatsApp:** ${settings.whatsappNumber}\n• **Hours:** 8:30AM - 6:00PM\n\nYou can also ask me about products and pricing!`;
      } else {
        // Discovery
        const question = getDiscoveryQuestions(mergedReq, lang, activeConversation);
        response = question || (lang === 'sinhala'
          ? `😊 ඔයාගේ requirement ගැන කෙටියෙන් කියන්න. CCTV, Alarm, Wi-Fi, Starlink — කොයි service ද ඕන?`
          : lang === 'singlish'
          ? `Oyage requirement eka kiyanna. CCTV, Alarm, Wi-Fi, Starlink — mokak da oney?`
          : `I'd be happy to help! Could you tell me more about what you need?\n\nFor example: "CCTV for my shop" or "4 cameras for home"`);
      }
    }

    setIsTyping(false);
    addMessageToConversation(convId, { role: 'assistant', content: response });
  }, [activeConversation, catalogProducts, packages, updateConversationState, addMessageToConversation, settings, createLead, getLeadByConversationId]);

  const sendMessage = useCallback(async () => {
    const text = inputText.trim();
    if (!text || !activeConversation) return;

    setInputText('');
    addMessageToConversation(activeConversation.id, { role: 'user', content: text });
    await processMessage(text);
  }, [inputText, activeConversation, addMessageToConversation, processMessage]);

  const handleQuickPrompt = useCallback(async (prompt: string) => {
    if (!activeConversation) return;
    addMessageToConversation(activeConversation.id, { role: 'user', content: prompt });
    await processMessage(prompt);
  }, [activeConversation, addMessageToConversation, processMessage]);

  // Voice Input
  const startVoice = useCallback(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || (window as unknown as { webkitSpeechRecognition: typeof SpeechRecognition }).webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      setVoiceError('Voice input not supported on this browser. Please type your message.');
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'si-LK'; // Sinhala

    recognition.onresult = (e: SpeechRecognitionEvent) => {
      const transcript = e.results[0][0].transcript;
      setInputText(transcript);
      setIsListening(false);
    };

    recognition.onerror = () => {
      // Try English if Sinhala fails
      recognition.lang = 'en-US';
      setIsListening(false);
    };

    recognition.onend = () => setIsListening(false);

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
    setVoiceError('');
  }, []);

  const stopVoice = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  // Text-to-speech
  const speakText = useCallback((text: string) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const stripped = text.replace(/\*\*|#{1,3}|_/g, '').substring(0, 500);
    const utterance = new SpeechSynthesisUtterance(stripped);
    utterance.lang = 'en-US';
    utterance.onend = () => setIsSpeaking(false);
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  }, []);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  }, []);

  // Quotation submit
  const submitQuotation = useCallback(() => {
    if (!quotationName.trim() || !quotationPhone.trim()) return;
    if (!activeConversation) return;

    updateConversationState(activeConversation.id, {
      customerName: quotationName,
      customerPhone: quotationPhone,
      customerEmail: quotationEmail,
      conversationPhase: 'quotation',
      isLead: true,
    });

    createLead({
      conversationId: activeConversation.id,
      customerName: quotationName,
      customerPhone: quotationPhone,
      customerEmail: quotationEmail,
      requirement: [
        activeConversation.requirement.serviceType,
        activeConversation.requirement.cameraQuantity ? `${activeConversation.requirement.cameraQuantity} cameras` : '',
        activeConversation.requirement.propertyType,
      ].filter(Boolean).join(', ') || 'CCTV / Security System',
      estimatedValue: activeConversation.estimatedValue,
      language: activeConversation.language,
      source: 'website',
      status: 'quotation-requested',
    });

    const lang = activeConversation.language;
    let confirmMsg = '';
    if (lang === 'sinhala') {
      confirmMsg = `✅ **Quotation Request Submit කළා!**\n\nK-Tec team ඔයාව ${quotationPhone} number ට ශ්‍රේෂ්ඨ 24 hours ඇතුළත contact කරනවා.\n\nReference: #QR-${Date.now().toString(36).toUpperCase()}`;
    } else if (lang === 'singlish') {
      confirmMsg = `✅ **Quotation Request submit kala!**\n\nK-Tec team oyawa ${quotationPhone} number eka ta 24 hours ater contact karanawa.\n\nReference: #QR-${Date.now().toString(36).toUpperCase()}`;
    } else {
      confirmMsg = `✅ **Quotation Request Submitted!**\n\nOur K-Tec team will contact you at ${quotationPhone} within 24 hours.\n\nReference: #QR-${Date.now().toString(36).toUpperCase()}`;
    }

    addMessageToConversation(activeConversation.id, { role: 'assistant', content: confirmMsg });
    setShowQuotationForm(false);
    setQuotationName('');
    setQuotationPhone('');
    setQuotationEmail('');
  }, [quotationName, quotationPhone, quotationEmail, activeConversation, updateConversationState, createLead, addMessageToConversation]);

  const handleNewChat = useCallback(() => {
    startNewConversation();
    setShowQuotationForm(false);
    setInputText('');
  }, [startNewConversation]);

  if (!settings.publicChatEnabled) return null;

  const messages = activeConversation?.messages || [];
  const showQuickPrompts = messages.length <= 1;

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isChatOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setActiveChatOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#7FA86A] rounded-full shadow-xl flex items-center justify-center hover:bg-[#658C53] transition-colors group"
            aria-label="Open K-Tec AI Chat"
          >
            <Sparkles size={24} className="text-white" />
            <span className="absolute -top-8 right-0 bg-[#1D1D1F] text-white text-xs px-2 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
              K-Tec AI
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: isMinimized ? 0 : 1, y: isMinimized ? 20 : 0, scale: isMinimized ? 0.95 : 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-6 right-6 z-50 w-full max-w-[420px] sm:max-w-[450px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ height: 'min(600px, calc(100vh - 100px))', maxHeight: '90vh' }}
          >
            {/* Header */}
            <div className="bg-[#1D1D1F] px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#7FA86A] flex items-center justify-center">
                  <Sparkles size={18} className="text-white" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">K-Tec AI Assistant</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-[#86868B] text-xs">Online · සිංහල · English · Singlish</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleNewChat}
                  className="p-1.5 text-[#86868B] hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  title="New Chat"
                >
                  <RefreshCw size={15} />
                </button>
                <button
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 text-[#86868B] hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  title="Minimize"
                >
                  <Minimize2 size={15} />
                </button>
                <button
                  onClick={() => setActiveChatOpen(false)}
                  className="p-1.5 text-[#86868B] hover:text-white transition-colors rounded-lg hover:bg-white/10"
                  title="Close"
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white">
              {messages.map(msg => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  onSpeak={settings.voiceOutputEnabled ? speakText : undefined}
                />
              ))}
              {isTyping && <TypingIndicator />}

              {/* Quick Prompts */}
              {showQuickPrompts && !isTyping && (
                <div className="mt-3">
                  <p className="text-[#86868B] text-xs mb-2 font-medium">Quick options:</p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {QUICK_PROMPTS.map(p => (
                      <button
                        key={p.label}
                        onClick={() => handleQuickPrompt(p.label)}
                        className="text-left px-2.5 py-2 bg-[#F5F5F7] hover:bg-[#7FA86A]/10 hover:border-[#7FA86A] border border-transparent rounded-xl text-xs text-[#1D1D1F] transition-all"
                      >
                        <span className="mr-1">{p.icon}</span>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quotation Form */}
              {showQuotationForm && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#F5F5F7] rounded-xl p-3 space-y-2"
                >
                  <p className="text-sm font-semibold text-[#1D1D1F]">📋 Quotation Request</p>
                  <input
                    type="text"
                    placeholder="ඔයාගේ නම / Your name *"
                    value={quotationName}
                    onChange={e => setQuotationName(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border border-[#E5E5E7] rounded-lg focus:outline-none focus:border-[#7FA86A]"
                  />
                  <input
                    type="tel"
                    placeholder="Phone number * (07X XXXXXXX)"
                    value={quotationPhone}
                    onChange={e => setQuotationPhone(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border border-[#E5E5E7] rounded-lg focus:outline-none focus:border-[#7FA86A]"
                  />
                  <input
                    type="email"
                    placeholder="Email (optional)"
                    value={quotationEmail}
                    onChange={e => setQuotationEmail(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-white border border-[#E5E5E7] rounded-lg focus:outline-none focus:border-[#7FA86A]"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={submitQuotation}
                      disabled={!quotationName.trim() || !quotationPhone.trim()}
                      className="flex-1 py-2 bg-[#7FA86A] text-white text-sm font-medium rounded-lg disabled:opacity-50 hover:bg-[#658C53] transition-colors"
                    >
                      Submit Request
                    </button>
                    <button
                      onClick={() => setShowQuotationForm(false)}
                      className="px-3 py-2 text-sm text-[#86868B] hover:text-[#1D1D1F] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Voice Error */}
            {voiceError && (
              <div className="px-4 py-2 bg-amber-50 border-t border-amber-100">
                <p className="text-amber-700 text-xs">{voiceError}</p>
              </div>
            )}

            {/* Speaking indicator */}
            {isSpeaking && (
              <div className="px-4 py-2 bg-[#7FA86A]/10 border-t border-[#7FA86A]/20 flex items-center justify-between">
                <span className="text-xs text-[#7FA86A] font-medium flex items-center gap-1.5">
                  <Volume2 size={13} className="animate-pulse" /> Speaking...
                </span>
                <button onClick={stopSpeaking} className="text-xs text-[#86868B] hover:text-[#1D1D1F]">
                  Stop
                </button>
              </div>
            )}

            {/* Input */}
            <div className="px-3 py-3 border-t border-[#F5F5F7] bg-white flex-shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Type in Sinhala, English or Singlish..."
                  rows={1}
                  className="flex-1 resize-none px-3 py-2 bg-[#F5F5F7] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30 max-h-24 overflow-y-auto"
                  style={{ lineHeight: '1.4' }}
                />
                {settings.voiceInputEnabled && (
                  <button
                    onClick={isListening ? stopVoice : startVoice}
                    className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors ${
                      isListening
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-[#F5F5F7] text-[#86868B] hover:text-[#1D1D1F]'
                    }`}
                    title={isListening ? 'Stop listening' : 'Voice input'}
                  >
                    {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                  </button>
                )}
                <button
                  onClick={sendMessage}
                  disabled={!inputText.trim() || isTyping}
                  className="w-9 h-9 flex-shrink-0 bg-[#7FA86A] text-white rounded-xl flex items-center justify-center disabled:opacity-50 hover:bg-[#658C53] transition-colors"
                >
                  <Send size={15} />
                </button>
              </div>
              <div className="mt-1.5 flex items-center justify-between">
                <p className="text-[10px] text-[#86868B]">K-Tec AI · Local Mode</p>
                <div className="flex gap-2">
                  <a href={`tel:${settings.contactNumber}`} className="text-[10px] text-[#7FA86A] flex items-center gap-0.5 hover:underline">
                    <Phone size={9} /> Call
                  </a>
                  <a href={`https://wa.me/94${settings.whatsappNumber?.replace(/^0/, '')}`} target="_blank" rel="noreferrer" className="text-[10px] text-green-600 flex items-center gap-0.5 hover:underline">
                    <MessageCircle size={9} /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
